import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Plus, Trash2, Languages, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SectionTitle, Card } from "../ui";

const PAGES = ["pets", "driving", "drivers-transport", "residency-documents", "cities-housing", "arrival-settling", "contact", "home"];
const INPUT = "w-full bg-white border border-sand-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sage/50";
const ES_INPUT = "w-full bg-sage-light/40 border border-sage/30 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sage/50";

function TranslatePanel({ onDone }) {
  const [job, setJob] = useState(null);
  const [polling, setPolling] = useState(false);

  const poll = () => api.get("/admin/translate/status").then((r) => {
    setJob(r.data);
    if (r.data.status === "running") {
      setPolling(true);
      setTimeout(poll, 3000);
    } else if (polling) {
      setPolling(false);
      if (r.data.status === "done") { toast.success("Spanish translation finished"); onDone(); }
      if (r.data.status === "failed") toast.error(`Translation failed: ${r.data.error || "unknown error"}`);
    }
  }).catch(() => setPolling(false));

  useEffect(() => { poll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async () => {
    try {
      const r = await api.post("/admin/translate/run");
      setJob(r.data);
      setPolling(true);
      toast.success("AI translation started — Spanish fields will fill in as it runs");
      setTimeout(poll, 3000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start translation"); }
  };

  const running = job?.status === "running";
  return (
    <Card className="mb-6 border-sage/40" data-testid="translate-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-medium text-charcoal-800 inline-flex items-center gap-2"><Languages size={16} className="text-sage-700" /> Spanish translations</h3>
          <p className="mt-1 text-xs text-charcoal-500 max-w-md">
            AI-translates every FAQ and page hero/intro that doesn't have Spanish yet (uses your Emergent LLM key). Existing Spanish text is never overwritten — edit any field below to fine-tune.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {running && <span className="text-xs text-charcoal-500" data-testid="translate-progress">{job.done || 0}/{job.total || "?"} translated…</span>}
          {!running && job?.status === "done" && <span className="text-xs text-sage-700">Up to date ✓</span>}
          <button data-testid="translate-run" onClick={run} disabled={running}
            className="inline-flex items-center gap-2 rounded-full bg-sage px-5 py-2 text-sm text-white font-medium hover:bg-sage-hover disabled:opacity-60">
            <RefreshCw size={14} className={running ? "animate-spin" : ""} /> {running ? "Translating…" : "AI-translate missing Spanish"}
          </button>
        </div>
      </div>
    </Card>
  );
}

export default function FaqManager() {
  const [faqs, setFaqs] = useState([]);
  const [newFaq, setNewFaq] = useState({ page: "pets", question: "", answer: "" });

  const load = () => api.get("/admin/faqs").then((res) => setFaqs(res.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newFaq.question) return;
    await api.post("/admin/faqs", { ...newFaq, order: 99 });
    toast.success("FAQ added");
    setNewFaq({ page: newFaq.page, question: "", answer: "" });
    load();
  };

  const update = async (f) => {
    await api.put(`/admin/faqs/${f.id}`, {
      question: f.question, answer: f.answer, page: f.page, order: f.order,
      question_es: f.question_es || "", answer_es: f.answer_es || "",
    });
    toast.success("FAQ saved");
  };

  const remove = async (id) => {
    await api.delete(`/admin/faqs/${id}`);
    toast.success("FAQ deleted");
    load();
  };

  const setField = (id, key, val) => setFaqs((arr) => arr.map((f) => (f.id === id ? { ...f, [key]: val } : f)));

  return (
    <div data-testid="admin-faqs">
      <Helmet><title>FAQ manager</title></Helmet>
      <SectionTitle title="FAQ manager" subtitle="Add, edit, and delete FAQs per page — English and Spanish." />

      <TranslatePanel onDone={load} />

      <Card className="mb-6">
        <h3 className="font-medium text-charcoal-800 mb-4">Add a new FAQ</h3>
        <div className="space-y-3">
          <select
            data-testid="faq-new-page"
            value={newFaq.page}
            onChange={(e) => setNewFaq({ ...newFaq, page: e.target.value })}
            className="bg-white border border-sand-200 rounded-lg px-3 py-2 text-sm"
          >
            {PAGES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <input
            data-testid="faq-new-question"
            placeholder="Question"
            value={newFaq.question}
            onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })}
            className={INPUT}
          />
          <textarea
            data-testid="faq-new-answer"
            placeholder="Answer"
            rows={2}
            value={newFaq.answer}
            onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
            className={INPUT}
          />
          <button data-testid="faq-add" onClick={add} className="inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2 text-sm text-white font-medium hover:bg-terracotta-hover">
            <Plus size={16} /> Add FAQ
          </button>
        </div>
      </Card>

      <div className="space-y-4">
        {faqs.map((f) => (
          <Card key={f.id}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wide text-sage-700 bg-sage-light rounded-full px-2 py-0.5">{f.page}</span>
              <button data-testid={`faq-delete-${f.id}`} onClick={() => remove(f.id)} className="text-charcoal-400 hover:text-destructive">
                <Trash2 size={16} />
              </button>
            </div>
            <input
              data-testid={`faq-q-${f.id}`}
              value={f.question}
              onChange={(e) => setField(f.id, "question", e.target.value)}
              className={`${INPUT} mb-2`}
            />
            <textarea
              data-testid={`faq-a-${f.id}`}
              rows={2}
              value={f.answer}
              onChange={(e) => setField(f.id, "answer", e.target.value)}
              className={INPUT}
            />
            <p className="mt-3 mb-1 text-[11px] uppercase tracking-wider text-sage-700 font-medium">Español</p>
            <input
              data-testid={`faq-qes-${f.id}`}
              placeholder="Pregunta (Spanish — leave blank to show English)"
              value={f.question_es || ""}
              onChange={(e) => setField(f.id, "question_es", e.target.value)}
              className={`${ES_INPUT} mb-2`}
            />
            <textarea
              data-testid={`faq-aes-${f.id}`}
              placeholder="Respuesta (Spanish)"
              rows={2}
              value={f.answer_es || ""}
              onChange={(e) => setField(f.id, "answer_es", e.target.value)}
              className={ES_INPUT}
            />
            <button data-testid={`faq-save-${f.id}`} onClick={() => update(f)} className="mt-2 rounded-full bg-sage px-5 py-2 text-sm text-white font-medium hover:bg-sage-hover">
              Save
            </button>
          </Card>
        ))}
      </div>
    </div>
  );
}
